import { useState, useCallback, useRef } from "react";
import { inventoryFetchClient, isNetworkFetchError } from "./inventoryFetchClient";
import { extractSectionId } from "./utils";
import { resolveItemFilePayload } from "./uploadItemFiles";
import {
  getIncompleteRequiredInventoryItems,
  isInventoryItemFilled,
} from "@/helpers/inventoryCatalog";
import type {
  InventoryFormState,
  InventoryInspectionOutput,
  InventoryFormSection,
  InventoryItemRow,
  InventoryItemPresence,
} from "@/types/custom-types";

type DeletedItemRef = {
  id: string;
  sectionId: string;
};

type SaveFullInspectionParams = {
  propertyId: string;
  mode: "create" | "edit";
  formState: InventoryFormState;
  formSections: InventoryFormSection[];
  inventory?: InventoryInspectionOutput | null;
  asDraft?: boolean;
  deletedSectionIds?: string[];
  deletedItemIds?: DeletedItemRef[];
  onProgress?: (done: number, total: number) => void;
};

const isValidItemRow = (row: InventoryItemRow): boolean =>
  Boolean(row.description?.trim());

const mapPresence = (
  value: InventoryItemPresence | null | undefined
): InventoryItemPresence | null => {
  if (value === "YES" || value === "NO" || value === "NOT_NEEDED") return value;
  return null;
};

const recoverCreatedInspection = async (
  propertyId: string
): Promise<InventoryInspectionOutput | null> => {
  try {
    const listed = await inventoryFetchClient<InventoryInspectionOutput[]>(
      `/properties/${propertyId}/inventory-inspections`,
      {
        method: "GET",
        operation: "listInventoryInspections",
      }
    );
    if (!listed?.length) return null;
    return [...listed].sort((a, b) => {
      const da = a.inspectionDate ? Date.parse(String(a.inspectionDate)) : 0;
      const db = b.inspectionDate ? Date.parse(String(b.inspectionDate)) : 0;
      if (db !== da) return db - da;
      return String(b.id).localeCompare(String(a.id));
    })[0];
  } catch {
    return null;
  }
};

export const useSaveFullInspection = (options?: {
  onError?: (error: Error) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const createdInspectionIdRef = useRef("");
  const createdSectionsRef = useRef<InventoryInspectionOutput["sections"]>([]);

  const saveFullInspection = useCallback(
    async ({
      propertyId,
      mode,
      formState,
      formSections,
      inventory,
      asDraft = false,
      deletedSectionIds = [],
      deletedItemIds = [],
      onProgress,
    }: SaveFullInspectionParams): Promise<string> => {
      setLoading(true);
      try {
        if (!asDraft) {
          const incompleteRequired =
            getIncompleteRequiredInventoryItems(formSections);
          if (incompleteRequired.length > 0) {
            throw new Error(
              "Required inventory items must be reviewed, including whether they are in the property and who provides them if missing."
            );
          }
        }

        let inspectionId = inventory?.id ?? createdInspectionIdRef.current;
        let serverSections: InventoryInspectionOutput["sections"] =
          createdSectionsRef.current;

        const inspectorName = formState.inspectorName.trim();
        const receiverName = formState.receiverName.trim();

        if (mode === "create" && !inspectionId) {
          try {
            const created = await inventoryFetchClient<InventoryInspectionOutput>(
              `/properties/${propertyId}/inventory-inspections`,
              {
                method: "POST",
                operation: "createInventoryInspection",
                variables: {
                  type: formState.inspectionType,
                  inspectionDate: formState.inspectionDate,
                  inspectorName,
                  receiverName,
                  generalNotes: formState.generalNotes,
                  isDraft: asDraft,
                },
              }
            );
            inspectionId = created.id;
            serverSections = created.sections ?? [];
          } catch (error) {
            if (!isNetworkFetchError(error)) throw error;
            const recovered = await recoverCreatedInspection(propertyId);
            if (!recovered?.id) throw error;
            inspectionId = recovered.id;
            serverSections = recovered.sections ?? [];
          }
          createdInspectionIdRef.current = inspectionId;
          createdSectionsRef.current = serverSections;
        } else if (inventory || inspectionId) {
          const id = inventory?.id ?? inspectionId;
          await inventoryFetchClient<InventoryInspectionOutput>(
            `/properties/${propertyId}/inventory-inspections/${id}`,
            {
              method: "PUT",
              operation: "updateInventoryInspection",
              variables: {
                type: formState.inspectionType,
                inspectionDate: formState.inspectionDate,
                inspectorName,
                receiverName,
                generalNotes: formState.generalNotes,
                isDraft: asDraft,
              },
            }
          );
          inspectionId = id;
          createdInspectionIdRef.current = id;
          try {
            const fresh = await inventoryFetchClient<InventoryInspectionOutput>(
              `/properties/${propertyId}/inventory-inspections/${id}`,
              {
                method: "GET",
                operation: "getInventoryInspection",
              }
            );
            serverSections = fresh.sections ?? inventory?.sections ?? [];
          } catch {
            serverSections = inventory?.sections ?? createdSectionsRef.current;
          }
          createdSectionsRef.current = serverSections;
        }

        if (!inspectionId) {
          throw new Error("No inspection id after save");
        }

        const sectionKeyToId: Record<string, string> = {};
        const serverItemsBySectionKey: Record<
          string,
          Array<{ id: string; title: string }>
        > = {};

        serverSections.forEach((section) => {
          sectionKeyToId[section.key] = section.id;
          serverItemsBySectionKey[section.key] = (section.items ?? []).map(
            (item) => ({ id: item.id, title: item.title })
          );
        });

        const totalItems = formSections.reduce(
          (sum, section) => sum + section.items.length,
          0
        );
        let savedItems = 0;
        onProgress?.(0, totalItems);

        for (let index = 0; index < formSections.length; index++) {
          const section = formSections[index];
          const existingId = section.id ?? sectionKeyToId[section.key];
          const endpoint = existingId
            ? `/properties/${propertyId}/inventory-inspections/${inspectionId}/sections/${existingId}`
            : `/properties/${propertyId}/inventory-inspections/${inspectionId}/sections`;

          const inspectionAfterSection =
            await inventoryFetchClient<InventoryInspectionOutput>(endpoint, {
              method: existingId ? "PUT" : "POST",
              operation: existingId
                ? "updateInventoryInspectionSection"
                : "createInventoryInspectionSection",
              variables: {
                key: section.key,
                title: section.title.trim() || section.key,
                order: index,
                isSystem: section.isSystem,
              },
            });

          const sectionId = extractSectionId(
            inspectionAfterSection,
            section.key,
            {
              id: existingId,
              title: section.title,
            }
          );

          if (sectionId) {
            sectionKeyToId[section.key] = sectionId;
          }
        }

        for (const section of formSections) {
          const sectionId = sectionKeyToId[section.key];
          if (!sectionId) continue;

          const usedServerItemIds = new Set<string>();
          const seededItems = serverItemsBySectionKey[section.key] ?? [];
          let order = 0;

          for (const row of section.items) {
            if (!isValidItemRow(row)) {
              savedItems += 1;
              onProgress?.(savedItems, totalItems);
              continue;
            }

            let matchedId = row.id;
            if (!matchedId) {
              const match = seededItems.find(
                (seeded) =>
                  seeded.title === row.description.trim() &&
                  !usedServerItemIds.has(seeded.id)
              );
              if (match) {
                matchedId = match.id;
                usedServerItemIds.add(match.id);
              }
            } else {
              usedServerItemIds.add(matchedId);
            }

            const shouldUpsert =
              !asDraft ||
              isInventoryItemFilled(row) ||
              Boolean(row.notes?.trim()) ||
              (row.newFiles?.length ?? 0) > 0 ||
              (row.existingFiles?.length ?? 0) > 0;

            if (shouldUpsert) {
              const filePayload = await resolveItemFilePayload(row);
              const presence = mapPresence(row.isPresentInProperty);
              const isPresent = presence === "YES";
              const quantityValue = isPresent
                ? row.quantity === ""
                  ? "1"
                  : String(row.quantity)
                : null;

              const endpoint = matchedId
                ? `/properties/${propertyId}/inventory-inspections/${inspectionId}/sections/${sectionId}/items/${matchedId}`
                : `/properties/${propertyId}/inventory-inspections/${inspectionId}/sections/${sectionId}/items`;

              const variables: Record<string, unknown> = {
                title: row.description.trim(),
                quantity: quantityValue,
                status: row.status || "NOT_EVALUATED",
                order,
                notes: row.notes?.trim() ? row.notes.trim() : undefined,
                isReviewed: isInventoryItemFilled(row) || row.isReviewed,
                isRequired: Boolean(row.isRequired),
                isPresentInProperty: presence,
                providedBy: presence === "NO" ? row.providedBy : null,
              };

              if (filePayload) {
                variables.retainedFileIds = filePayload.retainedFileIds;
                variables.uploadedFilesIds = filePayload.uploadedFilesIds;
              }

              await inventoryFetchClient(endpoint, {
                method: matchedId ? "PUT" : "POST",
                operation: matchedId
                  ? "updateInventoryInspectionItem"
                  : "createInventoryInspectionItem",
                variables,
              });
            }

            order += 1;
            savedItems += 1;
            onProgress?.(savedItems, totalItems);
          }
        }

        if (!asDraft) {
          const deletedSectionsSet = new Set<string>();
          for (const sectionId of deletedSectionIds) {
            if (deletedSectionsSet.has(sectionId)) continue;
            await inventoryFetchClient(
              `/properties/${propertyId}/inventory-inspections/${inspectionId}/sections/${sectionId}`,
              {
                method: "DELETE",
                operation: "deleteInventoryInspectionSection",
              }
            );
            deletedSectionsSet.add(sectionId);
          }

          const deletedItemsSet = new Set<string>();
          for (const ref of deletedItemIds) {
            if (deletedItemsSet.has(ref.id)) continue;
            await inventoryFetchClient(
              `/properties/${propertyId}/inventory-inspections/${inspectionId}/sections/${ref.sectionId}/items/${ref.id}`,
              {
                method: "DELETE",
                operation: "deleteInventoryInspectionItem",
              }
            );
            deletedItemsSet.add(ref.id);
          }
        }

        return inspectionId;
      } catch (error) {
        const err =
          error instanceof Error
            ? error
            : new Error("Failed to save inspection");
        options?.onError?.(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [options]
  );

  return { saveFullInspection, loading };
};
